import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts'
import { TrendingDown, TrendingUp, Minus } from 'lucide-react'
import { useGetFiiAnalysisQuery } from '@/resources/apis/fii-analysis'
import type { FiiAnalysisData, FiiTrend } from '@/types/FiiAnalysis'
import './fii-analysis-section.css'

interface FiiAnalysisSectionProps {
  ticker: string
  data?: FiiAnalysisData
}

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
}

function formatMonth(isoDate: string): string {
  const [year, month] = isoDate.split('-')
  return `${MONTH_LABELS[month] ?? month}/${year.slice(2)}`
}

function formatBRL(value: number | null): string {
  if (value === null) return '--'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

function formatPercent(value: number | null): string {
  if (value === null) return '--'
  return `${(value * 100).toFixed(2)}%`
}

const TREND_CONFIG: Record<FiiTrend, { label: string; icon: typeof TrendingUp; className: string }> = {
  growing:  { label: 'Crescendo',  icon: TrendingUp,   className: 'fii-analysis__trend--growing' },
  stable:   { label: 'Estável',    icon: Minus,         className: 'fii-analysis__trend--stable' },
  declining:{ label: 'Em queda',   icon: TrendingDown,  className: 'fii-analysis__trend--declining' },
}

export function FiiAnalysisSection({ ticker, data: externalData }: FiiAnalysisSectionProps) {
  const { data: fetchedData, isLoading, isError } = useGetFiiAnalysisQuery(ticker, !externalData)
  const data = externalData ?? fetchedData

  if (isLoading && !externalData) {
    return (
      <div className="market-details__card fii-analysis">
        <div className="market-details__card-header">
          <h2>Análise Mensal</h2>
        </div>
        <div className="fii-analysis__loading">Carregando análise...</div>
      </div>
    )
  }

  if ((isError && !externalData) || !data) {
    return (
      <div className="market-details__card fii-analysis">
        <div className="market-details__card-header">
          <h2>Análise Mensal</h2>
        </div>
        <div className="fii-analysis__empty">
          Análise ainda não disponível. Os dados são coletados diariamente da CVM.
        </div>
      </div>
    )
  }

  const { monthlyData, analysis } = data

  const chartData = monthlyData.map((d) => ({
    month: formatMonth(d.competence_month),
    'PL (R$)': d.net_worth,
    'Cotistas': d.total_shareholders,
    'DY (%)': d.dividend_yield_month !== null ? +(d.dividend_yield_month * 100).toFixed(4) : null,
  }))

  const latestMonth = monthlyData.at(-1)
  const trend = analysis?.trend
  const trendConfig = trend ? TREND_CONFIG[trend] : null
  const TrendIcon = trendConfig?.icon

  return (
    <div className="market-details__card fii-analysis">
      <div className="market-details__card-header">
        <div>
          <h2>Análise Mensal</h2>
          {latestMonth && (
            <p>Referência: {formatMonth(latestMonth.competence_month)}</p>
          )}
        </div>
        {analysis && trendConfig && TrendIcon && (
          <div className={`fii-analysis__trend ${trendConfig.className}`}>
            <TrendIcon size={14} />
            <span>{trendConfig.label}</span>
          </div>
        )}
      </div>

      {/* Métricas do mês mais recente */}
      {latestMonth && (
        <div className="fii-analysis__metrics">
          <div className="fii-analysis__metric">
            <span>Patrimônio Líquido</span>
            <strong>{formatBRL(latestMonth.net_worth)}</strong>
          </div>
          <div className="fii-analysis__metric">
            <span>Cotistas</span>
            <strong>{latestMonth.total_shareholders?.toLocaleString('pt-BR') ?? '--'}</strong>
          </div>
          <div className="fii-analysis__metric">
            <span>VP da Cota</span>
            <strong>
              {latestMonth.quota_value !== null
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(latestMonth.quota_value)
                : '--'}
            </strong>
          </div>
          <div className="fii-analysis__metric">
            <span>DY Mensal</span>
            <strong>{formatPercent(latestMonth.dividend_yield_month)}</strong>
          </div>
        </div>
      )}

      {/* Gráfico de evolução */}
      {chartData.length > 1 && (
        <div className="fii-analysis__chart">
          <p className="fii-analysis__chart-title">Evolução do PL</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatBRL(v)}
                width={72}
              />
              <Tooltip formatter={(v: number | undefined) => v != null ? formatBRL(v) : '--'} labelStyle={{ fontWeight: 600 }} />
              <Line
                type="monotone"
                dataKey="PL (R$)"
                stroke="var(--color-brand)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Análise do Claude */}
      {analysis && (
        <div className="fii-analysis__ai">
          <div className="fii-analysis__ai-header">
            <div className="fii-analysis__score">
              <span className="fii-analysis__score-label">Score</span>
              <span
                className="fii-analysis__score-value"
                data-score={
                  analysis.health_score >= 7 ? 'high' : analysis.health_score >= 4 ? 'mid' : 'low'
                }
              >
                {analysis.health_score}/10
              </span>
            </div>
          </div>

          <p className="fii-analysis__summary">{analysis.summary}</p>

          <div className="fii-analysis__points">
            {analysis.positive_points.length > 0 && (
              <div className="fii-analysis__points-group">
                <p className="fii-analysis__points-title fii-analysis__points-title--positive">
                  Positivos
                </p>
                <ul>
                  {analysis.positive_points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.negative_points.length > 0 && (
              <div className="fii-analysis__points-group">
                <p className="fii-analysis__points-title fii-analysis__points-title--negative">
                  Atenção
                </p>
                <ul>
                  {analysis.negative_points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <p className="fii-analysis__ai-footer">
            Gerado por IA · {new Date(analysis.generated_at).toLocaleDateString('pt-BR')}
          </p>
        </div>
      )}
    </div>
  )
}
